import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { ErrorAlert } from '../portfolio/PortfolioCommon'
import { Icon } from '../components/ui/Icon'
import { Breadcrumbs } from '../components/ui/Breadcrumbs'
import { useTenant } from '../tenancy/TenantProvider'
import { BoardChartSvg, boardChartFileName } from './BoardReportCharts'
import { ReportCanvas } from './ReportCanvas'
import { boardChartDefinition, boardChartsForPage, type BoardChartCode } from './board-chart-model'
import {
  completeReportingAssets,
  downloadReportingAsset,
  generateReportPack,
  getReportingSnapshot,
  listReportingSnapshots,
  previewReportPack,
  recordReportingDownload,
  uploadReportingAsset,
} from './reporting-api'
import { createStoreOnlyZip, renderReportPng, saveBlob, sha256 } from './report-export'
import {
  REPORT_PAGE_CATALOGUE,
  REPORT_PAGE_CODES,
  formatBoardDateTime,
  numberValue,
  pageDefinition,
  record,
  reportFileName,
  snapshotZipName,
  type ManagementStatement,
  type ReportPageCode,
  type ReportingPayload,
  type ReportingSnapshot,
} from './reporting-model'

function ReportingTabs({ active }: { active: 'create' | 'history' }) {
  return <nav className="reporting-tabs" aria-label="Board reporting">
    <Link className={active === 'create' ? 'active' : ''} to="/reports">Create report pack</Link>
    <Link className={active === 'history' ? 'active' : ''} to="/reports/history">Report history</Link>
  </nav>
}

function ReportDataSummary({ code, payload }: { code: ReportPageCode; payload: Record<string, unknown> }) {
  const portfolio = record(payload.portfolio)
  const governance = record(payload.governance)
  const strategy = record(payload.strategy)
  const organisationStrategy = record(strategy.organisationStrategy)
  const rows = code === 'executive_overview'
    ? [['Needs attention', numberValue(record(portfolio.positions).needsAttention)], ['Waiting on decisions', numberValue(governance.awaitingDecisions)], ['Material changes', numberValue(governance.materialChangesSincePrevious)]]
    : code === 'ai_portfolio_governance'
      ? [['Current AI uses', numberValue(portfolio.totalUses)], ['Live or pilot', numberValue(portfolio.liveOrPilot)], ['Strategy aligned', numberValue(portfolio.alignedUses)], ['Alignment not recorded', numberValue(portfolio.notYetAligned)]]
      : code === 'strategy_roadmap'
        ? [['Organisation priorities', numberValue(organisationStrategy.priorityCount)], ['Priorities linked to AI strategy', numberValue(organisationStrategy.linkedPriorityCount)], ['Strategic objectives', numberValue(strategy.objectiveCount)], ['Measures with a recorded update', numberValue(strategy.measuredCount)]]
        : code === 'risk_position'
          ? [['Current AI risks', numberValue(record(record(payload.risk).matrix).totalCurrent)], ['Rated risks', numberValue(record(record(payload.risk).matrix).ratedRiskCount)], ['Risks not yet rated', numberValue(record(record(payload.risk).matrix).notRatedCount)]]
          : [['Open management matters', numberValue(governance.openMatters)], ['Waiting on decisions', numberValue(governance.awaitingDecisions)], ['Overdue mandatory work', numberValue(governance.overdueMandatory)], ['Material changes', numberValue(governance.materialChangesSincePrevious)]]
  const explanation = code === 'executive_overview'
    ? 'Swell groups current recorded governance positions and decision queues into an executive attention summary.'
    : code === 'ai_portfolio_governance'
      ? 'Swell counts current AI Register records and their recorded lifecycle and strategy-alignment states.'
      : code === 'strategy_roadmap'
        ? 'Swell preserves the organisation’s approved priority wording, groups it by the customer-selected strategy type, and follows confirmed links through AI priorities, objectives, initiatives, AI uses and recorded benefit types.'
        : code === 'risk_position'
          ? 'Swell projects current authoritative residual risk ratings into the fixed three-by-three Board view and keeps recorded tolerance positions separate.'
          : 'Swell groups current management matters, authorised decisions, overdue obligations and recorded material changes.'
  return <details className="report-data-summary">
    <summary>How Swell calculated this page</summary>
    <div className="automation-trace">
      <p>{explanation}</p>
      <dl><div><dt>Rule</dt><dd>Current deterministic report aggregation</dd></div><div><dt>Sources</dt><dd>Recorded Register, governance, strategy and risk facts</dd></div><div><dt>Boundary</dt><dd>Snapshot values only; no hidden AI judgement</dd></div></dl>
    </div>
    <table><thead><tr><th scope="col">Measure</th><th scope="col">Recorded value</th></tr></thead><tbody>{rows.map(([label, value]) => <tr key={String(label)}><th scope="row">{label}</th><td>{value}</td></tr>)}</tbody></table>
  </details>
}

function ReportChartDownloads({ pageCode, payload }: { pageCode: ReportPageCode; payload: ReportingPayload }) {
  const chartCodes = boardChartsForPage(pageCode)
  const sourceRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const download = async (chartCode: BoardChartCode) => {
    const svg = sourceRef.current?.querySelector<SVGSVGElement>(`svg[data-board-chart="${chartCode}"]`)
    if (!svg) throw new Error('The chart is not ready to download')
    const png = await renderReportPng(svg)
    saveBlob(png, boardChartFileName(chartCode, payload.asOf))
  }
  return <div className="report-chart-downloads">
    <span>Standalone charts</span>
    <div>{chartCodes.map((chartCode) => <button key={chartCode} className="button secondary" type="button" onClick={() => {
      setError(null)
      void download(chartCode).catch((downloadError: unknown) => setError(downloadError instanceof Error ? downloadError.message : 'Chart download failed'))
    }}>Download {boardChartDefinition(chartCode).title} PNG</button>)}</div>
    {error ? <p role="alert">{error}</p> : null}
    <div ref={sourceRef} hidden>{chartCodes.map((chartCode) => <BoardChartSvg key={chartCode} code={chartCode} payload={payload} idPrefix={`export-${pageCode}`} />)}</div>
  </div>
}

function ReportPreviewPages({ pages, payload, snapshot, managementStatement = null }: { pages: ReportPageCode[]; payload: Record<string, unknown>; snapshot?: ReportingSnapshot; managementStatement?: ManagementStatement | null }) {
  return <div className="report-preview-pages" data-snapshot-render={snapshot?.id ?? 'preview'}>
    {pages.map((code) => <article className="report-preview-page" key={code} aria-labelledby={`preview-${code}`}>
      <div className="report-preview-heading">
        <span>{String(pageDefinition(code).number).padStart(2, '0')}</span>
        <h2 id={`preview-${code}`}>{pageDefinition(code).title}</h2>
      </div>
      <div className="report-canvas-frame">
        <ReportCanvas
          code={code}
          payload={payload as ReportingSnapshot['payload']}
          snapshotNo={snapshot?.snapshotNo}
          managementStatement={snapshot?.managementStatement ?? managementStatement}
        />
      </div>
      <ReportChartDownloads pageCode={code} payload={payload as ReportingPayload} />
      <ReportDataSummary code={code} payload={payload} />
    </article>)}
  </div>
}

async function downloadSnapshotZip(snapshot: ReportingSnapshot) {
  const files = await Promise.all(snapshot.assets.map(async (asset) => ({ name: asset.fileName, blob: await downloadReportingAsset(asset.storageObjectName) })))
  const zip = await createStoreOnlyZip(files)
  await recordReportingDownload(snapshot.id, 'zip')
  saveBlob(zip, snapshotZipName(snapshot.snapshotNo))
}

export function ReportsPage() {
  const { current, hasRole } = useTenant()
  const organisationId = current?.organisation.id ?? ''
  const canRead = Boolean(current)
  const canGenerate = hasRole('governance_manager')
  const queryClient = useQueryClient()
  const [pages, setPages] = useState<ReportPageCode[]>([...REPORT_PAGE_CODES])
  const [statement, setStatement] = useState('')
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof previewReportPack>> | null>(null)
  const [snapshot, setSnapshot] = useState<ReportingSnapshot | null>(null)
  const [assetError, setAssetError] = useState<Error | null>(null)
  const [assetProgress, setAssetProgress] = useState('')
  const generationStarted = useRef<string | null>(null)

  const previewMutation = useMutation({
    mutationFn: () => previewReportPack(organisationId, pages),
    onSuccess: (result) => { setPreview(result); setSnapshot(null); setAssetError(null) },
  })
  const generateMutation = useMutation({
    mutationFn: () => {
      if (!preview) throw new Error('Preview this report pack before generating it')
      return generateReportPack(organisationId, preview, statement)
    },
    onSuccess: (result) => { generationStarted.current = null; setSnapshot(result); setAssetError(null) },
  })

  const generateAssets = async (source: ReportingSnapshot) => {
    setAssetProgress('Creating report files')
    const currentSnapshot = await getReportingSnapshot(source.id)
    const existing = new Set(currentSnapshot.assets.map((asset) => asset.pageCode))
    const container = document.querySelector(`[data-snapshot-render="${source.id}"]`)
    if (!container) throw new Error('The generated report pages are not ready to export')
    for (const [index, code] of currentSnapshot.pages.entries()) {
      if (existing.has(code)) continue
      setAssetProgress(`Rendering page ${index + 1} of ${currentSnapshot.pages.length}: ${pageDefinition(code).title}`)
      const svg = container.querySelector<SVGSVGElement>(`svg[data-report-page="${code}"]`)
      if (!svg) throw new Error(`${pageDefinition(code).title} could not be found for export`)
      const png = await renderReportPng(svg)
      const fileName = reportFileName(currentSnapshot.snapshotNo, code)
      await uploadReportingAsset(currentSnapshot, code, fileName, png, await sha256(png))
    }
    setAssetProgress('Creating report files')
    const completed = await completeReportingAssets(currentSnapshot.id)
    setSnapshot(completed)
    setAssetProgress('')
    await queryClient.invalidateQueries({ queryKey: ['reporting-history', organisationId] })
  }

  useEffect(() => {
    if (!snapshot || snapshot.assetStatus === 'ready' || generationStarted.current === snapshot.id) return
    generationStarted.current = snapshot.id
    const timer = window.setTimeout(() => {
      void generateAssets(snapshot).catch((error: unknown) => {
        generationStarted.current = null
        setAssetProgress('')
        setAssetError(error instanceof Error ? error : new Error('Report asset generation failed'))
      })
    }, 50)
    return () => window.clearTimeout(timer)
  }, [snapshot]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!current) return <Navigate to="/workspace/create" replace />
  if (!canRead) return <Navigate to="/" replace />

  const togglePage = (code: ReportPageCode) => {
    setPages((selected) => selected.includes(code) ? selected.filter((item) => item !== code) : REPORT_PAGE_CODES.filter((item) => item === code || selected.includes(item)))
    setPreview(null)
    setSnapshot(null)
  }

  return <div className="page-stack reporting-page">
    <header className="reporting-page-header">
      <div><p className="eyebrow">Board reporting</p><h1>Build a board-ready report pack</h1><p>Choose the pages you want to include, review the information, then create the report pack.</p></div>
    </header>
    <ReportingTabs active="create" />
    <ErrorAlert error={previewMutation.error ?? generateMutation.error ?? assetError} />

    {!snapshot ? <section className="panel report-pack-config" aria-labelledby="report-pages-heading">
      <div className="section-heading"><div><p className="eyebrow">Report selection</p><h2 id="report-pages-heading">Choose report pages</h2><p>Pages will appear in the order shown below.</p></div><span className="status-pill neutral">{pages.length} selected</span></div>
      <div className="report-page-selector">
        {REPORT_PAGE_CATALOGUE.map((page) => <label key={page.code} className={pages.includes(page.code) ? 'selected' : ''}>
          <input type="checkbox" checked={pages.includes(page.code)} onChange={() => togglePage(page.code)} />
          <span className="report-page-number">{String(page.number).padStart(2, '0')}</span>
          <span><strong>{page.title}</strong><small>{page.question}</small></span>
        </label>)}
      </div>
      <label className="management-statement-field">
        <span>Management commentary</span>
        <textarea maxLength={800} rows={4} value={statement} onChange={(event) => { setStatement(event.target.value); setPreview(null) }} placeholder="Add a concise management interpretation for the Executive Overview." />
        <small>Recommended: one short paragraph or 2–4 concise points. {statement.length}/800 characters · author and time are retained when the snapshot is generated</small>
      </label>
      <div className="report-config-actions">
        <button className="button primary" type="button" disabled={!pages.length || previewMutation.isPending} onClick={() => previewMutation.mutate()}>{previewMutation.isPending ? 'Preparing preview…' : 'Preview report pack'}</button>
        <span>Preview does not create a Reporting Snapshot.</span>
      </div>
    </section> : null}

    {preview && !snapshot ? <section className="report-preview-section" aria-labelledby="report-preview-heading">
      <div className="section-heading"><div><p className="eyebrow">Preview</p><h2 id="report-preview-heading">One position across {preview.pages.length} {preview.pages.length === 1 ? 'page' : 'pages'}</h2><p>Prepared {formatBoardDateTime(preview.preparedAt)}. Creating the report pack will verify that this preview is still current.</p></div>
        {canGenerate ? <div className="report-create-action"><p>Once created, this report pack cannot be changed.</p><button className="button primary" type="button" disabled={generateMutation.isPending} onClick={() => generateMutation.mutate()}>{generateMutation.isPending ? 'Creating report files' : 'Create report pack'}</button></div> : <span className="status-pill neutral">Preview access</span>}
      </div>
      <ReportPreviewPages pages={preview.pages} payload={preview.payload} managementStatement={statement.trim() ? { text: statement.trim(), authorUserId: '', authorName: '', recordedAt: '' } : null} />
    </section> : null}

    {snapshot ? <section className="report-preview-section generated" aria-labelledby="generated-report-heading">
      <div className="report-generated-banner">
        <span className="generated-icon" aria-hidden="true"><Icon name={snapshot.assetStatus === 'ready' ? 'check' : 'reports'} size={22} /></span>
        <div><p className="eyebrow">Reporting Snapshot {snapshot.snapshotNo}</p><h2 id="generated-report-heading">{snapshot.assetStatus === 'ready' ? 'Ready to download' : 'Report files are still being prepared'}</h2><p>{snapshot.assetStatus === 'ready' ? 'The report pack contains the selected pages as PNG files in a ZIP download.' : assetProgress || 'Creating report files'}</p></div>
        {snapshot.assetStatus === 'ready' ? <button className="button primary" type="button" onClick={() => void downloadSnapshotZip(snapshot).catch((error: unknown) => setAssetError(error instanceof Error ? error : new Error('ZIP download failed')))}>Download report pack</button> : assetError ? <button className="button primary" type="button" onClick={() => { generationStarted.current = null; setSnapshot({ ...snapshot }) }}>Retry creating report files</button> : <span className="report-generation-spinner" aria-label="Creating report files" />}
      </div>
      <ReportPreviewPages pages={snapshot.pages} payload={snapshot.payload} snapshot={snapshot} />
    </section> : null}
  </div>
}

export function ReportHistoryPage() {
  const { current } = useTenant()
  const organisationId = current?.organisation.id ?? ''
  const canRead = Boolean(current)
  const historyQuery = useQuery({ queryKey: ['reporting-history', organisationId], queryFn: () => listReportingSnapshots(organisationId), enabled: Boolean(organisationId && canRead) })
  if (!current) return <Navigate to="/workspace/create" replace />
  if (!canRead) return <Navigate to="/" replace />
  return <div className="page-stack reporting-page">
    <Breadcrumbs items={[{ label: 'Reports', to: '/reports' }, { label: 'Report history' }]} />
    <header className="reporting-page-header"><div><p className="eyebrow">Board reporting</p><h1>Report history</h1><p>Return to the exact data and image assets generated for each immutable Reporting Snapshot.</p></div></header>
    <ReportingTabs active="history" />
    <ErrorAlert error={historyQuery.error} />
    <section className="panel report-history-panel">
      {historyQuery.isLoading ? <p>Loading report history…</p> : historyQuery.data?.length ? <div className="report-history-list">{historyQuery.data.map((item) => <Link key={item.id} to={`/reports/history/${item.id}`}>
        <span className="snapshot-number">{String(item.snapshotNo).padStart(4, '0')}</span>
        <span><strong>Reporting Snapshot {item.snapshotNo}</strong><small>{formatBoardDateTime(item.snapshotAt)} · {item.pages.length} {item.pages.length === 1 ? 'page' : 'pages'} · {item.generatedByName}</small></span>
        <span className={`status-pill ${item.assetStatus === 'ready' ? 'positive' : 'attention'}`}>{item.assetStatus === 'ready' ? 'Ready to download' : 'Report files are still being prepared'}</span>
        <Icon name="arrow-right" size={18} />
      </Link>)}</div> : <div className="report-history-empty"><Icon name="reports" size={26} /><h2>No Reporting Snapshots yet</h2><p>Preview and generate a report pack to establish the first immutable comparison point.</p><Link className="button primary" to="/reports">Create report pack</Link></div>}
    </section>
  </div>
}

export function ReportingSnapshotPage() {
  const { snapshotId = '' } = useParams()
  const { current } = useTenant()
  const canRead = Boolean(current)
  const [downloadError, setDownloadError] = useState<Error | null>(null)
  const snapshotQuery = useQuery({ queryKey: ['reporting-snapshot', snapshotId], queryFn: () => getReportingSnapshot(snapshotId), enabled: Boolean(snapshotId && canRead) })
  const snapshot = snapshotQuery.data
  if (!canRead) return <Navigate to="/" replace />
  return <div className="page-stack reporting-page">
    <Breadcrumbs items={[{ label: 'Reports', to: '/reports' }, { label: 'Report history', to: '/reports/history' }, { label: snapshot ? `Reporting Snapshot ${snapshot.snapshotNo}` : 'Reporting Snapshot' }]} />
    <header className="reporting-page-header"><div><p className="eyebrow">Board reporting</p><h1>{snapshot ? `Reporting Snapshot ${snapshot.snapshotNo}` : 'Reporting Snapshot'}</h1><p>{snapshot ? `Generated ${formatBoardDateTime(snapshot.snapshotAt)} from one reconciled reporting position.` : 'Loading immutable reporting position…'}</p></div></header>
    <ErrorAlert error={snapshotQuery.error ?? downloadError} />
    {snapshot ? <>
      <section className="report-snapshot-meta panel"><div><span>Asset status</span><strong>{snapshot.assetStatus === 'ready' ? 'Ready to download' : 'Report files are still being prepared'}</strong></div><div><span>Pages</span><strong>{snapshot.pages.length}</strong></div><div><span>Reporting date</span><strong>{snapshot.reportingDate}</strong></div>{snapshot.assetStatus === 'ready' ? <button className="button primary" type="button" onClick={() => void downloadSnapshotZip(snapshot).catch((error: unknown) => setDownloadError(error instanceof Error ? error : new Error('ZIP download failed')))}>Download report pack</button> : null}</section>
      <ReportPreviewPages pages={snapshot.pages} payload={snapshot.payload} snapshot={snapshot} />
    </> : null}
  </div>
}
